/**
 * JSON Workflow Service
 *
 * Main service for JSON-based workflow operations.
 * Orchestrates validation, conversion, and creation.
 */

import type { Node, Edge } from '@xyflow/react';
import {
  WorkflowJSON,
  SimplifiedWorkflowJSON,
  WorkflowJSONOptions,
  CreateWorkflowFromJSONResult,
  WorkflowJSONValidationResult,
  detectWorkflowFormat,
  WorkflowJSONFormat,
  isFullFormat,
  isSimplifiedFormat,
} from '../types';
import { validateWorkflowJSON, validateFullFormat, validateSimplifiedFormat } from '../validation';
import { convertSimplifiedToFull, convertFullToSimplified } from './simplifiedToFullConverter';
import { convertJsonToGraph, convertGraphToJson, JsonToGraphResult } from './jsonToGraphConverter';
import { autoLayoutNodes, fillMissingPositions } from './autoLayoutService';
import { assignMissingNodeIds, assignMissingEdgeIds } from './idGeneratorService';

/**
 * Main JSON Workflow Service class
 */
export class JSONWorkflowService {
  /**
   * Create workflow from JSON
   */
  async createFromJSON(
    json: string | WorkflowJSON | SimplifiedWorkflowJSON,
    options: WorkflowJSONOptions = {}
  ): Promise<CreateWorkflowFromJSONResult> {
    const {
      autoLayout = true,
      autoGenerateIds = true,
      validateOnly = false,
      returnFullFormat = true,
    } = options;

    try {
      // Parse if string
      let parsed: WorkflowJSON | SimplifiedWorkflowJSON;
      if (typeof json === 'string') {
        try {
          parsed = JSON.parse(json);
        } catch (e) {
          return {
            success: false,
            validation: {
              isValid: false,
              errors: [
                {
                  code: 'INVALID_JSON',
                  message: `Invalid JSON syntax: ${(e as Error).message}`,
                  suggestion: 'Check for syntax errors like missing commas, quotes, or brackets',
                },
              ],
              warnings: [],
            },
          };
        }
      } else {
        parsed = json;
      }

      // Validate
      const validation = validateWorkflowJSON(parsed);
      if (!validation.isValid) {
        return {
          success: false,
          validation,
        };
      }

      // If validate only, return here
      if (validateOnly) {
        return {
          success: true,
          validation,
        };
      }

      // Convert to full format if needed
      let workflowJSON: WorkflowJSON;
      const format = detectWorkflowFormat(parsed);

      if (format === 'simplified') {
        workflowJSON = convertSimplifiedToFull(parsed as SimplifiedWorkflowJSON, {
          autoLayout,
        });
      } else {
        workflowJSON = parsed as WorkflowJSON;
      }

      // Auto-generate IDs if enabled
      if (autoGenerateIds) {
        workflowJSON.nodes = assignMissingNodeIds(workflowJSON.nodes);
        workflowJSON.edges = assignMissingEdgeIds(workflowJSON.edges);
      }

      // Auto-layout if enabled
      if (autoLayout) {
        const layoutResult = autoLayoutNodes(workflowJSON.nodes, workflowJSON.edges);
        workflowJSON.nodes = layoutResult.nodes;
      }

      return {
        success: true,
        data: {
          id: '', // Will be set by backend
          workflow: workflowJSON,
        },
        validation,
      };
    } catch (error) {
      return {
        success: false,
        validation: {
          isValid: false,
          errors: [
            {
              code: 'INVALID_JSON',
              message: `Error processing workflow JSON: ${(error as Error).message}`,
            },
          ],
          warnings: [],
        },
      };
    }
  }

  /**
   * Validate JSON without creating
   */
  validateJSON(json: string | object): WorkflowJSONValidationResult {
    return validateWorkflowJSON(json);
  }

  /**
   * Detect format of workflow JSON
   */
  detectFormat(json: string | object): WorkflowJSONFormat {
    const parsed = typeof json === 'string' ? JSON.parse(json) : json;
    return detectWorkflowFormat(parsed);
  }

  /**
   * Convert simplified format to full format
   */
  convertToFullFormat(
    simplified: SimplifiedWorkflowJSON,
    options: { autoLayout?: boolean } = {}
  ): WorkflowJSON {
    return convertSimplifiedToFull(simplified, options);
  }

  /**
   * Convert full format to simplified format
   */
  convertToSimplifiedFormat(full: WorkflowJSON): SimplifiedWorkflowJSON {
    return convertFullToSimplified(full);
  }

  /**
   * Import JSON into editor (returns React Flow compatible structure)
   */
  importToEditor(
    json: string | WorkflowJSON | SimplifiedWorkflowJSON,
    options: { autoLayout?: boolean } = {}
  ): JsonToGraphResult {
    return convertJsonToGraph(json, options);
  }

  /**
   * Export editor state to JSON
   */
  exportFromEditor(
    nodes: Node[],
    edges: Edge[],
    metadata: Partial<WorkflowJSON>,
    format: 'full' | 'simplified' = 'full'
  ): WorkflowJSON | SimplifiedWorkflowJSON {
    const fullJson = convertGraphToJson(nodes, edges, metadata);

    if (format === 'simplified') {
      return convertFullToSimplified(fullJson);
    }

    return fullJson;
  }

  /**
   * Check if JSON is in full format
   */
  isFullFormat(json: unknown): json is WorkflowJSON {
    return isFullFormat(json as WorkflowJSON | SimplifiedWorkflowJSON);
  }

  /**
   * Check if JSON is in simplified format
   */
  isSimplifiedFormat(json: unknown): json is SimplifiedWorkflowJSON {
    return isSimplifiedFormat(json as WorkflowJSON | SimplifiedWorkflowJSON);
  }

  /**
   * Normalize workflow JSON (ensure consistent structure)
   */
  normalizeWorkflow(
    json: WorkflowJSON,
    options: { autoLayout?: boolean; autoGenerateIds?: boolean } = {}
  ): WorkflowJSON {
    const { autoLayout = false, autoGenerateIds = true } = options;

    let workflow = { ...json };

    // Generate missing IDs
    if (autoGenerateIds) {
      workflow.nodes = assignMissingNodeIds(workflow.nodes);
      workflow.edges = assignMissingEdgeIds(workflow.edges);
    }

    // Fill missing positions
    if (autoLayout) {
      workflow.nodes = fillMissingPositions(workflow.nodes, workflow.edges);
    }

    return workflow;
  }
}

// Export singleton instance
export const jsonWorkflowService = new JSONWorkflowService();

// Export individual functions for direct use
export { validateWorkflowJSON, validateFullFormat, validateSimplifiedFormat };
export { convertSimplifiedToFull, convertFullToSimplified };
export { convertJsonToGraph, convertGraphToJson };
export { autoLayoutNodes, fillMissingPositions };
